import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Power } from "lucide-react";
import { Badge, Switch } from "@/components/ui";
import { adminService } from "@/services/adminService";
import { toastApiError } from "@/lib/errorMessages";
import type { Activity } from "@/types";
import { StaffConfigRow } from "./StaffConfigRow";

interface AdminActivityActiveSectionProps {
  activity: Activity;
}

/** Staff kill-switch: inactive activities cannot be opened by partners nor listed. */
export function AdminActivityActiveSection({
  activity,
}: AdminActivityActiveSectionProps) {
  const { t } = useTranslation("activities");
  const queryClient = useQueryClient();

  const toggleActiveMutation = useMutation({
    mutationFn: (is_active: boolean) =>
      adminService.updateActivityConfig(activity.id, { is_active }),
    onSuccess: async () => {
      toast.success(t("detail.adminActiveToastSaved"));
      await queryClient.invalidateQueries({
        queryKey: ["activity", activity.id],
      });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "activities"] });
    },
    onError: (err) => {
      toastApiError(err, "updateActivity");
    },
  });

  return (
    <StaffConfigRow
      icon={Power}
      title={t("detail.adminActiveTitle")}
      hint={t("detail.adminActiveHint")}
      actions={
        <Switch
          checked={activity.is_active}
          disabled={toggleActiveMutation.isPending}
          aria-label={t("detail.adminActiveToggleAria")}
          onChange={(event) => {
            toggleActiveMutation.mutate(event.target.checked);
          }}
        />
      }
    >
      <Badge
        variant={activity.is_active ? "success" : "default"}
        size="sm"
        dot
      >
        {activity.is_active ? t("card.active") : t("card.inactive")}
      </Badge>
    </StaffConfigRow>
  );
}
